import { MenuProps, Menu as MuiMenu, styled, SxProps, Theme } from '@mui/material';
import * as React from 'react';

import type { IconElement } from '../icons/IconElement';
import { BaseButtonProps } from './BaseButton';
import { NeutralButton } from './NeutralButton';
import { PrimaryButton } from './PrimaryButton';
import { SecondaryButton } from './SecondaryButton';
import { TertiaryButton } from './TertiaryButton';

type ButtonAppearance =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'neutral'
    | 'destructivePrimary'
    | ' destructiveTertiary';
type Props = React.PropsWithChildren<
    {
        id?: string;
        appearance?: ButtonAppearance;
        closeOnClick?: boolean;
        size?: BaseButtonProps['size'];
        /** Drawn before the label at the button's icon size; without a `label`, the button shows only the icon. */
        icon?: IconElement;
        /** Drawn after the label at the button's icon size, such as a dropdown chevron. */
        endIcon?: IconElement;
        style?: BaseButtonProps['style'];
        disabled?: boolean;
        menuProps?: Partial<MenuProps>;
        isLoading?: boolean;
    } & (
        | { label: string | JSX.Element; 'aria-label'?: string }
        | {
              label?: undefined;
              /** The button's name for screen readers, which skip its icon: an icon-only menu button needs it. */
              'aria-label': string;
          }
    )
>;

const ICON_BUTTON_SIDE = { small: '24px', medium: '32px', large: '40px' } as const;

/**
 * A menu button is a button that opens up a menu when clicked on it.
 *
 * The children of the `MenuButton` are the `MenuItem`s.
 *
 * Example:
 *
 * ```tsx
 * <MenuButton label="click me">
 *      <MenuItem>option 1</MenuItem>
 *      <MenuItem>option 2</MenuItem>
 * </MenuButton>
 * ```
 */
export const MenuButton: React.FunctionComponent<Props> = ({ closeOnClick = true, ...props }): JSX.Element => {
    const { children, menuProps, ...buttonProps } = props;
    /**
     * A reference to the DOM element. This DOM element is used by the Menu's anchorEl property
     * to position the menu relative to the button.
     *
     * See https://reactjs.org/docs/hooks-reference.html#useref
     * See https://material-ui.com/api/menu/
     */
    const ref = React.useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const handleClose = React.useCallback(() => {
        setIsOpen(false);
    }, []);
    const handleClick = React.useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            // A link item's router handler has run already; leave a link's default, e.g. cmd-click opening a tab.
            if (!(e.target instanceof Element && e.target.closest('a[href]'))) {
                e.preventDefault();
            }
            if (closeOnClick) {
                setIsOpen(false);
            }
        },
        [closeOnClick],
    );
    const handleOpen = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
    }, []);

    const showAsIconButton = buttonProps.label === undefined && buttonProps.icon != null;

    // Square, at the icon buttons' sizes: beside a `SecondaryIconButton` of the same size it lines up.
    const iconButtonSide = ICON_BUTTON_SIDE[buttonProps.size ?? 'large'];
    const iconButtonStyles: SxProps<Theme> = showAsIconButton
        ? {
              minWidth: 0,
              width: iconButtonSide,
              height: iconButtonSide,
              padding: 0,
              '& .MuiButton-startIcon': {
                  margin: 0,
              },
              // Without a label beside it, the small button's shift to the left would put the icon off centre.
              '& .MuiButton-startIcon > svg': {
                  margin: 0,
              },
          }
        : {};

    function getButtonComponent(buttonProps: Props, ref: React.Ref<HTMLButtonElement>): JSX.Element {
        switch (buttonProps.appearance) {
            case 'primary': {
                return (
                    <PrimaryButton
                        ref={ref}
                        onClick={handleOpen}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        disableTouchRipple
                        startIcon={buttonProps.icon}
                        sx={iconButtonStyles}
                        {...buttonProps}
                        style={{ whiteSpace: 'nowrap', ...buttonProps.style }}
                    >
                        {buttonProps.label}
                    </PrimaryButton>
                );
            }
            case 'secondary': {
                return (
                    <SecondaryButton
                        ref={ref}
                        onClick={handleOpen}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        disableTouchRipple
                        startIcon={buttonProps.icon}
                        sx={iconButtonStyles}
                        {...buttonProps}
                        style={{ whiteSpace: 'nowrap', ...buttonProps.style }}
                    >
                        {buttonProps.label}
                    </SecondaryButton>
                );
            }
            case 'tertiary': {
                return (
                    <TertiaryButton
                        ref={ref}
                        onClick={handleOpen}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        disableTouchRipple
                        startIcon={buttonProps.icon}
                        sx={iconButtonStyles}
                        {...buttonProps}
                        style={{ whiteSpace: 'nowrap', ...buttonProps.style }}
                    >
                        {buttonProps.label}
                    </TertiaryButton>
                );
            }
            case 'neutral': {
                return (
                    <NeutralButton
                        ref={ref}
                        onClick={handleOpen}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        disableTouchRipple
                        startIcon={buttonProps.icon}
                        sx={iconButtonStyles}
                        {...buttonProps}
                        style={{ whiteSpace: 'nowrap', ...buttonProps.style }}
                    >
                        {buttonProps.label}
                    </NeutralButton>
                );
            }
            default: {
                return (
                    <PrimaryButton
                        ref={ref}
                        onClick={handleOpen}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        disableTouchRipple
                        startIcon={buttonProps.icon}
                        sx={iconButtonStyles}
                        {...buttonProps}
                        style={{ whiteSpace: 'nowrap', ...buttonProps.style }}
                    >
                        {buttonProps.label}
                    </PrimaryButton>
                );
            }
        }
    }

    return (
        <>
            <Menu
                anchorEl={ref.current} // TODO - fix useRef
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 0, vertical: -8 }}
                elevation={1}
                onClick={handleClick}
                {...menuProps}
            >
                {children}
            </Menu>
            {getButtonComponent(buttonProps, ref)}
        </>
    );
};

const Menu = styled(MuiMenu)({
    '& .MuiList-padding': {
        padding: 0,
    },
});
